import { useCallback, useEffect, useMemo, useState } from "react";
import usePlanApi from "../api/use-plan-api";
import type { BillingPlanSummary } from "../types/billing";
import { BILLING_PLAN_REFRESH_EVENT } from "../utils/billing-plan-events";

const useCurrentPlan = (brandId?: string | null) => {
  const { getCurrentPlan } = usePlanApi();
  const [plan, setPlan] = useState<BillingPlanSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCurrentPlan(brandId);
      setPlan(response);
      if (!response) {
        setError("Unable to load billing plan.");
      }
    } catch (err) {
      console.error("Failed to refresh billing plan", err);
      setError("Unable to load billing plan.");
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [brandId, getCurrentPlan]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handlePlanRefresh = () => {
      void refresh();
    };

    window.addEventListener(BILLING_PLAN_REFRESH_EVENT, handlePlanRefresh);
    return () =>
      window.removeEventListener(BILLING_PLAN_REFRESH_EVENT, handlePlanRefresh);
  }, [refresh]);

  return useMemo(
    () => ({
      plan,
      loading,
      error,
      refresh,
      isBeta: Boolean(plan?.betaEnabled),
      isFree: (plan?.planTier ?? "free") === "free",
      isPro: plan?.planTier === "pro",
      isAgency: plan?.planTier === "agency",
      hasReachedBrandLimit:
        plan?.limits.brands != null &&
        (plan?.usage.brands ?? 0) >= plan.limits.brands,
      hasReachedGenerationLimit:
        plan?.limits.monthlyGenerations != null &&
        (plan?.usage.monthlyGenerations ?? 0) >= plan.limits.monthlyGenerations,
      hasReachedKnowledgeSourceLimit:
        plan?.limits.knowledgeSources != null &&
        (plan?.usage.knowledgeSourcesForActiveBrand ?? 0) >=
          plan.limits.knowledgeSources,
    }),
    [error, loading, plan, refresh]
  );
};

export default useCurrentPlan;
